import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Alert, Spinner, Button, Form, Badge } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const CompanyProfileHub: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState('company_setup');
  const [associations, setAssociations] = useState<any[]>([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [discoveredAgents, setDiscoveredAgents] = useState<any[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [scriptResults, setScriptResults] = useState<any>(null);
  const [loadingScript, setLoadingScript] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [savingToDatabase, setSavingToDatabase] = useState(false);
  const [saveResults, setSaveResults] = useState<any>(null);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Company profile form data
  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    license_number: '',
    mls_id: '',
    office_short_id: '',
    mls_access: false
  });

  // MLS Integration form data
  const [mlsData, setMlsData] = useState({
    association_id: '',
    agreed_to_terms: false
  });

  useEffect(() => {
    if (!authLoading && user) {
      setLoading(false);
      // Load existing company profile data
      loadExistingProfile();
    }
  }, [authLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadExistingProfile = async () => {
    try {
      // First check localStorage
      const savedCompanyData = localStorage.getItem('brokerCompanyData');
      if (savedCompanyData) {
        setCompanyData(JSON.parse(savedCompanyData));
      }
      
      const savedMlsData = localStorage.getItem('brokerMlsData');
      if (savedMlsData) {
        const parsed = JSON.parse(savedMlsData);
        setMlsData({
          association_id: parsed.association_id || '',
          agreed_to_terms: parsed.agreed_to_terms || false
        });
      }

      // Try to load from API
      const authToken = localStorage.getItem('auth_token');
      if (authToken && user?.id) {
        try {
          const response = await fetch(`http://localhost:8080/api/broker/my-brokerages`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.brokerages && data.brokerages.length > 0) {
              // We have a brokerage, so the profile has been saved
              console.log('✅ Found existing brokerage:', data.brokerages[0]);
            }
          }
        } catch (error) {
          console.log('Could not load profile from API:', error);
        }
      }
    } catch (error) {
      console.error('Error loading existing profile:', error);
    }
  };

  // Load associations when moving to MLS stage
  useEffect(() => {
    if (currentStage === 'mls_integration' && associations.length === 0) {
      loadAssociations();
    }
  }, [currentStage]);

  const loadAssociations = async () => {
    try {
      setLoadingAssociations(true);
      
      // Use absolute URL to ensure it hits the correct server
      const response = await fetch('http://localhost:8080/api/associations');
      
      const data = await response.json();
      
      if (data.success && data.data && data.data.associations) {
        setAssociations(data.data.associations);
      } else {
        console.error('❌ [MLS INTEGRATION] Failed to load associations:', data.message || 'Invalid response format');
        console.error('❌ [MLS INTEGRATION] Full response:', data);
        setError('Failed to load MLS associations');
      }
    } catch (error) {
      console.error('❌ [MLS INTEGRATION] Error loading associations:', error);
      setError('Error loading MLS associations: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoadingAssociations(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Save company data to localStorage for backwards compatibility
      localStorage.setItem('brokerCompanyData', JSON.stringify(companyData));
      
      // Try to save to database via API
      try {
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch('http://localhost:8080/api/broker/company-profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            ...companyData,
            user_id: user.id
          })
        });
        
        const data = await response.json();
        
        if (data.success && data.data.brokerage_id) {
          console.log('✅ [COMPANY PROFILE] Successfully saved to database:', data.data);
          // Save the brokerage ID and name for use in Broker Intelligence Hub
          localStorage.setItem('brokerageId', data.data.brokerage_id);
          localStorage.setItem('brokerageName', companyData.company_name || 'Your Brokerage');
          console.log('✅ [COMPANY PROFILE] Saved brokerage ID:', data.data.brokerage_id);
          console.log('✅ [COMPANY PROFILE] Saved brokerage name:', companyData.company_name);
        } else {
          console.warn('⚠️ [COMPANY PROFILE] API save failed, continuing with localStorage:', data);
        }
      } catch (apiError) {
        console.warn('⚠️ [COMPANY PROFILE] API not available, continuing with localStorage:', apiError);
        // Continue without failing - the localStorage data will be used
      }
      
      // Always move to next stage (don't block on API issues)
      setCurrentStage('mls_integration');
      console.log('✅ [COMPANY PROFILE] Advanced to MLS Integration stage');
    } catch (error) {
      console.error('❌ [COMPANY PROFILE] Error saving company profile:', error);
      setError('Error saving company profile: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMlsInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setMlsData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMlsFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📋 [MLS INTEGRATION] Form submitted:', mlsData);
    
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Save MLS data to localStorage for backwards compatibility
      localStorage.setItem('brokerMlsData', JSON.stringify(mlsData));
      
      // Get the existing company data
      const existingCompanyData = JSON.parse(localStorage.getItem('brokerCompanyData') || '{}');
      
      // Try to save combined data to database via API
      try {
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch('http://localhost:8080/api/broker/company-profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            ...existingCompanyData,
            ...mlsData,
            user_id: user.id
          })
        });
        
        const data = await response.json();
        
        if (data.success && data.data.brokerage_id) {
          console.log('✅ [MLS INTEGRATION] Successfully saved to database:', data.data);
          // Save the brokerage ID for use in Broker Intelligence Hub
          localStorage.setItem('brokerageId', data.data.brokerage_id);
          console.log('✅ [MLS INTEGRATION] Saved brokerage ID:', data.data.brokerage_id);
        } else {
          console.warn('⚠️ [MLS INTEGRATION] API save failed, continuing with localStorage:', data);
        }
      } catch (apiError) {
        console.warn('⚠️ [MLS INTEGRATION] API not available, continuing with localStorage:', apiError);
        // Continue without failing - the localStorage data will be used
      }
      
      // Skip steps 3 & 4, go directly to Dashboard Access
      setCurrentStage('dashboard_access');
      console.log('✅ [MLS INTEGRATION] Advanced to Dashboard Access stage');
    } catch (error) {
      console.error('❌ [MLS INTEGRATION] Error saving MLS data:', error);
      setError('Error saving MLS integration data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const discoverAgents = async () => {
    try {
      setLoadingAgents(true);
      setError(null);
      console.log('🔍 [AGENT DISCOVERY] Starting agent discovery...');
      
      // Get the selected association details
      const selectedAssociation = associations.find(a => 
        (a.association_id || a.id).toString() === mlsData.association_id
      );
      
      console.log('🔍 [AGENT DISCOVERY] Selected association:', selectedAssociation);
      
      const requestData = {
        association_id: mlsData.association_id,
        association_name: selectedAssociation?.association_name || 'Unknown',
        company_name: companyData.company_name
      };
      
      console.log('🔍 [AGENT DISCOVERY] Request data:', requestData);
      
      const response = await fetch('http://localhost:8080/api/agent-discovery/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      console.log('🔍 [AGENT DISCOVERY] Response:', data);
      
      if (data.success) {
        setDiscoveredAgents(data.data.agents);
        console.log('✅ [AGENT DISCOVERY] Found', data.data.agents.length, 'agents');
      } else {
        setError('Failed to discover agents');
      }
    } catch (error) {
      console.error('❌ [AGENT DISCOVERY] Error:', error);
      setError('Error discovering agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadSampleAgents = async () => {
    try {
      setLoadingAgents(true);
      console.log('📋 [AGENT DISCOVERY] Loading sample agents...');
      
      const response = await fetch('http://localhost:8080/api/agent-discovery/sample-data');
      const data = await response.json();
      
      if (data.success) {
        setDiscoveredAgents(data.data.agents);
        console.log('✅ [AGENT DISCOVERY] Loaded', data.data.agents.length, 'sample agents');
      }
    } catch (error) {
      console.error('❌ [AGENT DISCOVERY] Error loading sample agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const runMlsScript = async () => {
    if (!user) {
      setScriptError('User not authenticated');
      return;
    }
    
    try {
      setLoadingScript(true);
      setScriptError(null);
      setScriptResults(null);
      setSaveResults(null);
      console.log(`🐍 Running Enhanced Brokerage Analysis for: ${companyData.company_name}...`);
      
      // DEBUG: Override email for testing until authentication is fixed
      const testEmailOverride = 'boykingroup@gmail.com';
      console.log(`🔧 [DEBUG] Using email override: ${testEmailOverride} (instead of ${user.email})`);
      
      console.log('🐍 Making request to:', 'http://localhost:8080/api/agent-discovery/run-script');
      console.log('🐍 Request payload:', {
        association_id: mlsData.association_id,
        office_name: companyData.company_name,
        office_short_id: companyData.office_short_id,
        mls_id: companyData.mls_id,
        user_email: testEmailOverride
      });

      const response = await fetch('http://localhost:8080/api/agent-discovery/run-script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          association_id: mlsData.association_id,
          office_name: companyData.company_name,
          office_short_id: companyData.office_short_id,
          mls_id: companyData.mls_id,
          user_email: testEmailOverride  // Use override for testing
        })
      });
      
      console.log('🐍 Response status:', response.status);
      console.log('🐍 Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🐍 Script response:', data);
      
      if (data.success) {
        setScriptResults(data.data);
        console.log('✅ Enhanced Brokerage Analysis completed successfully');
        if (data.data.verification && data.data.verification.email_verified) {
          console.log('✅ Email verification successful');
        }
        if (data.data.agents && data.data.agents.length > 0) {
          setDiscoveredAgents(data.data.agents);
          // Clear any previous selection when new agents are discovered
          setSelectedAgents(new Set());
          setSelectAll(false);
        }
      } else {
        // Handle verification failure specifically
        if (data.verification_required) {
          setScriptError(`Email Verification Failed: ${data.message}. Please ensure your email (${user?.email}) is registered with this office in the MLS system.`);
        } else {
          setScriptError(data.message || data.error || 'Script execution failed');
        }
        console.error('❌ Script failed:', data.message || data.error);
      }
    } catch (error) {
      console.error('❌ Error running script:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Connection failed: Could not connect to localhost:8080. Please ensure the Python MLS service is running.';
        } else if (error.message.includes('HTTP')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      setScriptError('Error running MLS script: ' + errorMessage);
    } finally {
      setLoadingScript(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      // Select all agents
      const allAgentIds = new Set(discoveredAgents.map(agent => agent.id || agent.mls_agent_id || agent.name));
      setSelectedAgents(allAgentIds);
    } else {
      // Deselect all agents
      setSelectedAgents(new Set());
    }
  };

  const handleSelectAgent = (agentId: string, checked: boolean) => {
    const newSelection = new Set(selectedAgents);
    if (checked) {
      newSelection.add(agentId);
    } else {
      newSelection.delete(agentId);
    }
    setSelectedAgents(newSelection);
    
    // Update selectAll state
    setSelectAll(newSelection.size === discoveredAgents.length);
  };

  const saveToDatabase = async () => {
    if (!user) {
      setSaveResults({ success: false, message: 'User not authenticated' });
      return;
    }
    
    if (selectedAgents.size === 0) {
      setScriptError('Please select at least one agent to save');
      return;
    }
    
    try {
      setSavingToDatabase(true);
      setSaveResults(null);
      console.log('💾 Saving selected agents to database...');
      console.log('📊 Selected agents:', Array.from(selectedAgents));
      
      // Filter to only selected agents
      const selectedAgentData = discoveredAgents.filter(agent => 
        selectedAgents.has(agent.id || agent.mls_agent_id || agent.name)
      );
      
      console.log('📊 Selected agent data:', selectedAgentData);
      
      const response = await fetch('http://localhost:8080/api/agent-discovery/save-to-database', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          office_name: scriptResults.office_name,
          office_id: scriptResults.office_id,
          agents: selectedAgentData,
          user_id: user.id,
          selected_count: selectedAgents.size,
          total_discovered: discoveredAgents.length
        })
      });
      
      const data = await response.json();
      console.log('💾 Save response:', data);
      
      if (data.success) {
        setSaveResults(data.data);
        console.log('✅ Selected agents saved to database successfully');
        
        // Reset selection after successful save
        setSelectedAgents(new Set());
        setSelectAll(false);
      } else {
        setScriptError(data.message || 'Failed to save to database');
        console.error('❌ Database save failed:', data.message);
      }
    } catch (error) {
      console.error('❌ Error saving to database:', error);
      setScriptError('Error saving to database: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setSavingToDatabase(false);
    }
  };

  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Authentication Required</Alert.Heading>
        <p>Please log in to access the broker dashboard.</p>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Initializing Company Profile Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h2>🏢 Broker Company Setup</h2>
              <p className="mb-0">Welcome {user.name}! Let's set up your brokerage profile.</p>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Progress Steps */}
              <div className="mb-4">
                <div className="progress-steps d-flex justify-content-between">
                  <div className={`step ${currentStage === 'company_setup' ? 'active' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-label">Company Profile</span>
                  </div>
                  <div className={`step ${currentStage === 'mls_integration' ? 'active' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-label">MLS Integration</span>
                  </div>
                  {/* Steps 3 & 4 disabled - will be added later
                  <div className={`step ${currentStage === 'agent_discovery' ? 'active' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-label">Agent Discovery</span>
                  </div>
                  <div className={`step ${currentStage === 'billing_setup' ? 'active' : ''}`}>
                    <span className="step-number">4</span>
                    <span className="step-label">Billing Setup</span>
                  </div>
                  */}
                  <div className={`step ${currentStage === 'dashboard_access' ? 'active' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-label">Dashboard Access</span>
                  </div>
                </div>
              </div>

              {/* Company Setup Form */}
              {currentStage === 'company_setup' && (
                <div>
                  <h4>📋 Company Information</h4>
                  <Form onSubmit={handleFormSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="company_name"
                            value={companyData.company_name}
                            onChange={handleInputChange}
                            placeholder="Enter your brokerage name"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>License Number *</Form.Label>
                          <Form.Control
                            type="text"
                            name="license_number"
                            value={companyData.license_number}
                            onChange={handleInputChange}
                            placeholder="Broker license number"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Company Address *</Form.Label>
                      <Form.Control
                        type="text"
                        name="company_address"
                        value={companyData.company_address}
                        onChange={handleInputChange}
                        placeholder="Full business address"
                        required
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company Phone *</Form.Label>
                          <Form.Control
                            type="tel"
                            name="company_phone"
                            value={companyData.company_phone}
                            onChange={handleInputChange}
                            placeholder="(555) 123-4567"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company Email *</Form.Label>
                          <Form.Control
                            type="email"
                            name="company_email"
                            value={companyData.company_email}
                            onChange={handleInputChange}
                            placeholder="info@yourbrokerage.com"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>MLS ID *</Form.Label>
                          <Form.Control
                            type="text"
                            name="mls_id"
                            value={companyData.mls_id}
                            onChange={handleInputChange}
                            placeholder="CO627182000"
                            required
                          />
                          <Form.Text className="text-muted">
                            Your MLS identification number (e.g., CO627182000)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Office Short ID *</Form.Label>
                          <Form.Control
                            type="text"
                            name="office_short_id"
                            value={companyData.office_short_id}
                            onChange={handleInputChange}
                            placeholder="crcm01"
                            required
                          />
                          <Form.Text className="text-muted">
                            Your MLS office short ID (e.g., crcm01)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="mls_access"
                        checked={companyData.mls_access}
                        onChange={handleInputChange}
                        label="I have MLS access and want to integrate it with KairosIQ"
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-between">
                      <div></div>
                      <Button type="submit" variant="primary" size="lg" disabled={loading}>
                        {loading ? 'Saving...' : 'Save & Continue to MLS Integration →'}
                      </Button>
                    </div>
                  </Form>
                </div>
              )}

              {/* MLS Integration Stage */}
              {currentStage === 'mls_integration' && (
                <div>
                  <h4>🔗 MLS Integration Setup</h4>
                  <p>Configure your Multiple Listing Service (MLS) integration with KairosIQ.</p>
                  
                  <div className="alert alert-info">
                    <strong>Great!</strong> You indicated you have MLS access. Let's get you connected.
                  </div>

                  {loadingAssociations && (
                    <div className="text-center mb-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Loading MLS associations...</span>
                    </div>
                  )}

                  {!loadingAssociations && associations.length === 0 && (
                    <div className="alert alert-warning">
                      <strong>No associations loaded.</strong> 
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="ms-2"
                        onClick={loadAssociations}
                      >
                        Retry Loading Associations
                      </Button>
                    </div>
                  )}

                  {associations.length > 0 && (
                    <div className="alert alert-success">
                      <small>✅ Loaded {associations.length} MLS associations</small>
                    </div>
                  )}

                  <Form onSubmit={handleMlsFormSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>MLS Association *</Form.Label>
                      <Form.Select
                        name="association_id"
                        value={mlsData.association_id}
                        onChange={handleMlsInputChange}
                        required
                        disabled={loadingAssociations}
                      >
                        <option value="">Select your MLS association</option>
                        {associations
                          .filter(assoc => assoc.state) // Only show associations with state data
                          .map(association => (
                            <option 
                              key={association.id} 
                              value={association.association_id || association.id}
                            >
                              {association.association_name} ({association.city}, {association.state})
                            </option>
                          ))
                        }
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select the MLS association your brokerage belongs to
                      </Form.Text>
                    </Form.Group>

                    {/* MLS Login ID field removed - duplicate not needed */}

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="agreed_to_terms"
                        checked={mlsData.agreed_to_terms}
                        onChange={handleMlsInputChange}
                        label="I understand KairosIQ will use secure API connections to access MLS data and agree to the terms of service"
                        required
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => setCurrentStage('company_setup')}
                      >
                        ← Back to Company Setup
                      </Button>
                      <Button 
                        variant="primary" 
                        size="lg"
                        type="submit"
                        disabled={loadingAssociations || loading}
                      >
                        {loading ? 'Saving...' : 'Continue to Agent Discovery →'}
                      </Button>
                    </div>
                  </Form>

                  {/* Show selected association info */}
                  {mlsData.association_id && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6>Selected Association:</h6>
                      <small>
                        {associations.find(a => 
                          (a.association_id || a.id).toString() === mlsData.association_id
                        )?.association_name || 'Unknown Association'}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {/* Agent Discovery Stage */}
              {currentStage === 'agent_discovery' && (
                <div>
                  <h4>👥 Agent Discovery & Import</h4>
                  <p>Import your existing agents and discover new talent for your brokerage.</p>
                  
                  <div className="alert alert-info">
                    <strong>MLS Association:</strong> {associations.find(a => 
                      (a.association_id || a.id).toString() === mlsData.association_id
                    )?.association_name || 'Not selected'}
                    <br />
                    <strong>Company:</strong> {companyData.company_name}
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="card mb-3">
                        <div className="card-body">
                          <h6>🔍 Discover MLS Agents</h6>
                          <p>Find agents in your MLS association</p>
                          <Button 
                            variant="primary"
                            onClick={discoverAgents}
                            disabled={loadingAgents}
                          >
                            {loadingAgents ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Discovering...
                              </>
                            ) : 'Discover Agents'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card mb-3">
                        <div className="card-body">
                          <h6>📋 Test with Sample Data</h6>
                          <p>Load sample agents for testing</p>
                          <Button 
                            variant="outline-success"
                            onClick={loadSampleAgents}
                            disabled={loadingAgents}
                          >
                            Load Sample Agents
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Discovered Agents Table */}
                  {discoveredAgents.length > 0 && (
                    <div className="mt-4">
                      <h5>📊 Discovered Agents ({discoveredAgents.length})</h5>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>
                                <Form.Check 
                                  type="checkbox"
                                  checked={selectAll}
                                  onChange={(e) => handleSelectAll(e.target.checked)}
                                  title="Select All"
                                />
                              </th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>License #</th>
                              <th>Office</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {discoveredAgents.map((agent, index) => {
                              const agentId = agent.id || agent.mls_agent_id || agent.name;
                              return (
                                <tr key={agentId || index}>
                                  <td>
                                    <Form.Check 
                                      type="checkbox"
                                      checked={selectedAgents.has(agentId)}
                                      onChange={(e) => handleSelectAgent(agentId, e.target.checked)}
                                    />
                                  </td>
                                  <td>{agent.name}</td>
                                  <td>{agent.email}</td>
                                  <td>{agent.phone}</td>
                                  <td>{agent.license_number}</td>
                                  <td>{agent.office}</td>
                                  <td>
                                    <Badge bg="success">{agent.status || 'Active'}</Badge>
                                  </td>
                                  <td>
                                    <Button variant="outline-primary" size="sm">
                                      View Details
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <Button 
                              variant="primary" 
                              className="me-2"
                              onClick={saveToDatabase}
                              disabled={selectedAgents.size === 0 || savingToDatabase}
                            >
                              {savingToDatabase ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-users me-2"></i>
                                  Prepare Invites ({selectedAgents.size})
                                </>
                              )}
                            </Button>
                            
                            {saveResults && saveResults.pending_invites > 0 && (
                              <Button 
                                variant="success" 
                                className="me-2"
                                onClick={() => {
                                  // Call send-invites endpoint
                                  fetch('http://localhost:8080/api/agent-discovery/send-invites', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      brokerage_id: saveResults.brokerage_id,
                                      user_id: user?.id
                                    })
                                  })
                                  .then(res => res.json())
                                  .then(data => {
                                    if (data.success) {
                                      alert(`✅ ${data.data.invites_ready} invitations ready! Check console for invite URLs.`);
                                      console.log('📧 Invitation URLs:', data.data.invitations);
                                    } else {
                                      alert('❌ Failed to prepare invitations');
                                    }
                                  })
                                  .catch(err => {
                                    console.error('Error:', err);
                                    alert('❌ Error preparing invitations');
                                  });
                                }}
                              >
                                <i className="fas fa-envelope me-2"></i>
                                Send {saveResults.pending_invites} Invites
                              </Button>
                            )}
                            <Button variant="outline-primary" className="me-2">
                              <i className="fas fa-download me-2"></i>
                              Export List
                            </Button>
                            {selectedAgents.size > 0 && (
                              <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => {
                                  setSelectedAgents(new Set());
                                  setSelectAll(false);
                                }}
                              >
                                <i className="fas fa-times me-1"></i>
                                Clear Selection
                              </Button>
                            )}
                          </div>
                          <div className="text-muted">
                            <small>
                              {selectedAgents.size} of {discoveredAgents.length} agents selected
                            </small>
                          </div>
                        </div>
                        
                        {selectedAgents.size > 0 && (
                          <div className="mt-2">
                            <small className="text-info">
                              💡 Selected agents will be saved to your brokerage dashboard for management and billing.
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Run Python Script Button */}
                  <div className="mt-4 p-3 bg-light rounded">
                    <h6>🐍 Advanced: Run Enhanced Brokerage Analysis</h6>
                    <p className="mb-2">Execute the Enhanced Brokerage Analysis script to get real MLS data for any office</p>
                    <Button 
                      variant="warning"
                      size="sm"
                      onClick={runMlsScript}
                      disabled={loadingScript || !mlsData.association_id || !companyData.company_name}
                    >
                      {loadingScript ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Running Script...
                        </>
                      ) : 'Run Enhanced Brokerage Analysis'}
                    </Button>
                    
                    {/* Script Results */}
                    {scriptError && (
                      <div className="alert alert-danger mt-3">
                        <strong>Script Error:</strong> {scriptError}
                      </div>
                    )}
                    
                    {scriptResults && (
                      <div className="alert alert-success mt-3">
                        <strong>Enhanced Brokerage Analysis Results:</strong>
                        <div className="mt-2">
                          <small>
                            <strong>Office:</strong> {scriptResults.office_name}<br/>
                            <strong>Office Short ID:</strong> {scriptResults.office_short_id}<br/>
                            <strong>MLS ID:</strong> {scriptResults.mls_id}<br/>
                            <strong>Agents Found:</strong> {scriptResults.agents_found || 0}<br/>
                            <strong>Enhanced Fields:</strong> {scriptResults.enhanced_fields?.length || 0}<br/>
                            <strong>Script Version:</strong> {scriptResults.script_version}<br/>
                            
                            {scriptResults.verification && (
                              <div className="mt-2 p-2 bg-light rounded">
                                <strong>🔐 Verification Status:</strong><br/>
                                {scriptResults.verification.email_verified ? (
                                  <>
                                    <span className="text-success">✅ Email Verified</span><br/>
                                    <strong>Verified Agent:</strong> {scriptResults.verification.verified_agent?.Name}<br/>
                                    <strong>Agent ID:</strong> {scriptResults.verification.verified_agent?.ShortId}
                                  </>
                                ) : (
                                  <span className="text-warning">⚠️ No Email Verification</span>
                                )}
                              </div>
                            )}
                            
                            {scriptResults.financial_scenarios && (
                              <div className="mt-2">
                                <strong>Financial Projections:</strong><br/>
                                <small>
                                  Monthly: ${scriptResults.financial_scenarios.current_pricing?.monthly || 0}<br/>
                                  Annual: ${scriptResults.financial_scenarios.current_pricing?.annual || 0}
                                </small>
                              </div>
                            )}
                          </small>
                        </div>
                        
                        <div className="mt-3">
                          <Button 
                            variant="success"
                            onClick={saveToDatabase}
                            disabled={savingToDatabase || saveResults}
                            className="me-2"
                          >
                            {savingToDatabase ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving to Database...
                              </>
                            ) : saveResults ? (
                              '✅ Saved to Database'
                            ) : (
                              '💾 Save to Database'
                            )}
                          </Button>
                          
                          {scriptResults.raw_import_records && (
                            <details className="mt-2">
                              <summary>View Raw Data ({scriptResults.raw_import_records.length} records)</summary>
                              <pre className="bg-light p-2 mt-1" style={{fontSize: '10px', maxHeight: '300px', overflow: 'auto'}}>
                                {JSON.stringify(scriptResults.raw_import_records.slice(0, 2), null, 2)}
                                {scriptResults.raw_import_records.length > 2 && `\n... and ${scriptResults.raw_import_records.length - 2} more records`}
                              </pre>
                            </details>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {saveResults && (
                      <div className="alert alert-info mt-3">
                        <strong>📋 Agent Processing Results:</strong>
                        <div className="mt-2">
                          <small>
                            <strong>Brokerage:</strong> {saveResults.brokerage_name}<br/>
                            <strong>Total Agents:</strong> {saveResults.total_agents}<br/>
                            <strong>Existing Users:</strong> {saveResults.existing_users}<br/>
                            <strong>Pending Invites:</strong> {saveResults.pending_invites}<br/>
                            <strong>Brokerage ID:</strong> {saveResults.brokerage_id}
                          </small>
                        </div>
                        {saveResults.pending_invites > 0 ? (
                          <div className="mt-2">
                            <small className="text-primary">
                              📧 Ready to send invitations! Click the "Send Invites" button above to generate invitation links for your agents.
                            </small>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <small className="text-success">
                              ✅ All selected agents already have accounts and have been linked to your brokerage!
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between mt-4">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setCurrentStage('mls_integration')}
                    >
                      ← Back to MLS Integration
                    </Button>
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={() => setCurrentStage('billing_setup')}
                      disabled={discoveredAgents.length === 0}
                    >
                      Continue to Billing Setup →
                    </Button>
                  </div>
                </div>
              )}

              {/* Billing Setup Stage */}
              {currentStage === 'billing_setup' && (
                <div>
                  <h4>💳 Billing & Subscription Setup</h4>
                  <p>Choose your KairosIQ plan and set up billing information.</p>
                  
                  <div className="row">
                    <div className="col-md-4">
                      <div className="card mb-3">
                        <div className="card-body text-center">
                          <h6>Starter</h6>
                          <h4>$99/month</h4>
                          <p>Up to 5 agents</p>
                          <Button variant="outline-primary">Select Plan</Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card mb-3 border-primary">
                        <div className="card-body text-center">
                          <h6>Professional</h6>
                          <h4>$199/month</h4>
                          <p>Up to 25 agents</p>
                          <Button variant="primary">Select Plan</Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card mb-3">
                        <div className="card-body text-center">
                          <h6>Enterprise</h6>
                          <h4>Custom</h4>
                          <p>Unlimited agents</p>
                          <Button variant="outline-primary">Contact Sales</Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setCurrentStage('mls_integration')}
                    >
                      ← Back to MLS Integration
                    </Button>
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={() => setCurrentStage('dashboard_access')}
                    >
                      Continue to Dashboard →
                    </Button>
                  </div>
                </div>
              )}

              {/* Dashboard Access Stage */}
              {currentStage === 'dashboard_access' && (
                <div>
                  <h4>🎉 Onboarding Complete!</h4>
                  
                  <div className="alert alert-success">
                    <h5>🏢 Your brokerage profile has been successfully configured!</h5>
                    <p>Click the button below to access your Broker Dashboard and start managing your agents.</p>
                    <ul className="mt-3">
                      <li>✅ Company profile created</li>
                      <li>✅ MLS integration configured</li>
                      <li>✅ Ready to add agents</li>
                    </ul>
                  </div>

                  <div className="d-flex justify-content-between mt-4">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setCurrentStage('mls_integration')}
                    >
                      ← Back to MLS Integration
                    </Button>
                    <Button 
                      variant="success" 
                      size="lg"
                      onClick={() => {
                        console.log('🎉 [COMPANY PROFILE] Broker onboarding completed!');
                        // Navigate to Broker Intelligence Hub
                        navigate('/broker-intelligence');
                      }}
                    >
                      Complete Setup & Go to Dashboard →
                    </Button>
                  </div>
                </div>
              )}

              {/* Debug section removed - showing incorrect test data */}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style dangerouslySetInnerHTML={{
        __html: `
          .progress-steps {
            margin-bottom: 2rem;
          }
          .step {
            text-align: center;
            flex: 1;
            position: relative;
          }
          .step-number {
            display: inline-block;
            width: 40px;
            height: 40px;
            line-height: 40px;
            border-radius: 50%;
            background: #e9ecef;
            color: #6c757d;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .step.active .step-number {
            background: #007bff;
            color: white;
          }
          .step-label {
            display: block;
            font-size: 12px;
            color: #6c757d;
          }
          .step.active .step-label {
            color: #007bff;
            font-weight: bold;
          }
        `
      }} />
    </div>
  );
};

export default CompanyProfileHub;